import { useRestApiProvider } from "@jugl-web/rest-api";
import { TicketListItemModel } from "@jugl-web/rest-api/tickets/models/TicketListItem";
import { WithOptimistic } from "@jugl-web/utils";
import debounce from "lodash/debounce";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { ticketMutations$ } from "../events/ticketMutation";
import { TicketMutationEvent } from "../types/TicketMutationEvent";
import { buildGetTicketsQueryParams } from "../utils/buildGetTicketsQueryParams";
import { doesTicketMatchListCriteria } from "../utils/doesTicketMatchListCriteria";
import { UseTicketListOptions } from "./useTicketList/types";

type UseSubscribeToTicketListMutationsOptions = Pick<
  UseTicketListOptions,
  "entityId" | "filter" | "meId" | "contactId" | "searchQuery" | "sort"
> & {
  hasMore: boolean;
  setTickets: Dispatch<SetStateAction<WithOptimistic<TicketListItemModel>[]>>;
  setTotalCount: Dispatch<SetStateAction<number>>;
};

/**
 * Subscribes to ticket mutations and syncs a `useTicketList` instance via
 * `setTickets` / `setTotalCount`.
 */
export const useSubscribeToTicketListMutations = ({
  entityId,
  sort = "desc",
  hasMore,
  filter,
  meId,
  contactId,
  searchQuery,
  setTickets,
  setTotalCount,
}: UseSubscribeToTicketListMutationsOptions) => {
  const { ticketsApi } = useRestApiProvider();
  const [fetchTickets] = ticketsApi.useLazyGetTicketsQuery();
  const totalCountRequestRef = useRef<ReturnType<typeof fetchTickets> | null>(
    null
  );

  const refreshTotalCount = useCallback(async () => {
    const queryParams = buildGetTicketsQueryParams({
      filter,
      meId,
      contactId,
      searchQuery,
      sort,
    });

    totalCountRequestRef.current?.abort();

    const request = fetchTickets(
      {
        entityId,
        ...queryParams,
        page: 1,
        page_size: 1,
        meta: { silent: true },
      },
      false
    );

    totalCountRequestRef.current = request;

    const response = await request;

    if (response.status === "fulfilled" && response.data) {
      setTotalCount(response.data.total_entries);
    }
  }, [
    contactId,
    entityId,
    fetchTickets,
    filter,
    meId,
    searchQuery,
    setTotalCount,
    sort,
  ]);

  const debouncedRefreshTotalCount = useMemo(
    () => debounce(refreshTotalCount, 6000),
    [refreshTotalCount]
  );

  const applyTicketPatch = useCallback(
    (ticket: TicketListItemModel) => {
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticket.id ? { ...ticket, _optimistic: false } : t
        )
      );
    },
    [setTickets]
  );

  const removeTicket = useCallback(
    (ticketId: string) => {
      setTickets((prev) => {
        if (!prev.some((t) => t.id === ticketId)) {
          return prev;
        }

        return prev.filter((t) => t.id !== ticketId);
      });
    },
    [setTickets]
  );

  const upsertTicket = useCallback(
    (ticket: TicketListItemModel) => {
      setTickets((prev) => {
        if (prev.some((t) => t.id === ticket.id)) {
          return prev;
        }

        if (sort === "desc") {
          return [{ ...ticket, _optimistic: false }, ...prev];
        }

        if (!hasMore) {
          return [...prev, { ...ticket, _optimistic: false }];
        }

        return prev;
      });
    },
    [hasMore, setTickets, sort]
  );

  const handleTicketMutation = useCallback(
    (event: TicketMutationEvent) => {
      if (event.type === "deleted") {
        removeTicket(event.ticketId);
        debouncedRefreshTotalCount();
        return;
      }

      const isMine =
        !!event.isLocal ||
        (event.type === "created" && event.ticket.created_by.user_id === meId);

      if (filter === "recents" && !isMine) {
        applyTicketPatch(event.ticket);
        return;
      }

      const ticketMatchesList = doesTicketMatchListCriteria(event.ticket, {
        filter,
        meId,
        contactId,
        searchQuery,
      });

      if (event.type === "created") {
        if (ticketMatchesList) {
          upsertTicket(event.ticket);
          debouncedRefreshTotalCount();
        }

        return;
      }

      if (ticketMatchesList) {
        applyTicketPatch(event.ticket);
        upsertTicket(event.ticket);
        debouncedRefreshTotalCount();
        return;
      }

      removeTicket(event.ticket.id);
      debouncedRefreshTotalCount();
    },
    [
      applyTicketPatch,
      removeTicket,
      upsertTicket,
      debouncedRefreshTotalCount,
      filter,
      meId,
      contactId,
      searchQuery,
    ]
  );

  useEffect(() => {
    const subscription = ticketMutations$.subscribe(handleTicketMutation);

    return () => {
      subscription.unsubscribe();
      debouncedRefreshTotalCount.cancel();
      totalCountRequestRef.current?.abort();
    };
  }, [handleTicketMutation, debouncedRefreshTotalCount]);
};
